/**
 * Opportunity Detail Screen - MOBILE ONLY
 * Quick check view - key facts, owners, ideas, navigate deeper.
 */

import React, { useCallback, useState } from 'react';
import { View, Text, ScrollView, Pressable, ActivityIndicator, RefreshControl, Linking, Alert } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { color, icon } from '../../../shared/tokens/mobile';
import { OpportunityStatus, UpdateOpportunityRequest } from '../../../shared/types/opportunity';
import { authApi } from '../../../shared/utils/api';
import { logger } from '../../../shared/utils/logger';
import { formatCurrency, formatPricePerAcre, formatOpportunityLocation } from '../../../shared/utils/formatters';
import { useOpportunity } from '../../../hooks/useOpportunity';
import { useOpportunityActions } from '../../../hooks/useOpportunityActions';
import { ScreenContainer, ScreenHeader, ListCard, ListRow, SectionHeader } from '../../../components/v2/mobile';
import { OpportunityActionsMenu, DeleteConfirmModal, EditOpportunityModal, PassOpportunityModal } from '../../../components/v2/opportunity';
import { styles } from '../../../components/v2/opportunity/opportunityDetailStyles';
import { ParcelDetail, CountyRecordLinks } from '../../../components/v2/opportunity/OpportunityDetailComponents';
import PhotoGallery from '../../../components/v2/opportunity/PhotoGallery';
import VoiceNotes from '../../../components/v2/opportunity/VoiceNotes';
import FieldNotes from '../../../components/v2/opportunity/FieldNotes';

// Status config with new terminology
const STATUS_CONFIG: Record<OpportunityStatus, { label: string; color: string }> = {
  evaluating: { label: 'Evaluating', color: color.warning },
  under_contract: { label: 'Under Contract', color: color.info },
  owned: { label: 'Owned', color: color.success },
  selling: { label: 'Selling', color: color.warning },
  sold: { label: 'Sold', color: color.textMuted },
  passed: { label: 'Passed', color: color.textMuted },
};

export default function OpportunityDetailScreen() {
  const router = useRouter();
  const { id, portfolioId } = useLocalSearchParams<{ id: string; portfolioId?: string }>();
  
  const oppId = id ? parseInt(id, 10) : 0;
  const portId = portfolioId ? parseInt(portfolioId, 10) : 0;
  const { opportunity, loading, error, fetchOpportunity } = useOpportunity(portId, oppId);

  // Modal states
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPassModal, setShowPassModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Actions hook
  const {
    deleting,
    linkingParcel,
    deleteOpportunity,
    linkParcelData,
    refreshParcelData,
    passOpportunity,
  } = useOpportunityActions({
    portfolioId: portId,
    opportunityId: oppId,
    onDeleted: () => router.back(),
    onUpdated: fetchOpportunity,
  });

  const handleBack = useCallback(() => router.back(), [router]);
  const handleMore = useCallback(() => setShowActionsMenu(true), []);
  
  const handleListing = useCallback(() => {
    const url = opportunity?.listing_url || opportunity?.zillow_url;
    if (url) Linking.openURL(url);
  }, [opportunity]);

  const handlePass = useCallback(() => {
    setShowPassModal(true);
  }, []);

  const handleLinkParcel = useCallback(async () => {
    setShowActionsMenu(false);
    const result = await linkParcelData();
    if (result.success) {
      Alert.alert('Success', 'Parcel data linked successfully');
    } else {
      Alert.alert('Error', result.error || 'Failed to link parcel data');
    }
  }, [linkParcelData]);

  const handleRefreshParcel = useCallback(async () => {
    setShowActionsMenu(false);
    const result = await refreshParcelData();
    if (result.success) {
      Alert.alert('Success', 'Parcel data refreshed');
    } else {
      Alert.alert('Error', result.error || 'Failed to refresh parcel data');
    }
  }, [refreshParcelData]);

  const handleEditRequest = useCallback(() => {
    setShowActionsMenu(false);
    setTimeout(() => setShowEditModal(true), 100);
  }, []);

  const handleSaveEdit = useCallback(async (data: UpdateOpportunityRequest): Promise<boolean> => {
    setSaving(true);
    try {
      await authApi.updateOpportunity(portId, oppId, data);
      logger.info('Opportunity updated', 'opportunity', { opportunityId: oppId });
      await fetchOpportunity();
      return true;
    } catch (err) {
      logger.error('Failed to update opportunity', err, 'opportunity');
      Alert.alert('Error', 'Failed to update opportunity');
      return false;
    } finally {
      setSaving(false);
    }
  }, [portId, oppId, fetchOpportunity]);

  const handleDeleteRequest = useCallback(() => {
    setShowActionsMenu(false);
    setTimeout(() => setShowDeleteConfirm(true), 100);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    const result = await deleteOpportunity();
    if (!result.success) {
      Alert.alert('Error', result.error || 'Failed to delete opportunity');
    }
    setShowDeleteConfirm(false);
  }, [deleteOpportunity]);

  // Loading
  if (loading) {
    return (
      <ScreenContainer>
        <ScreenHeader title="" onBack={handleBack} loading />
        <View style={styles.center}>
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      </ScreenContainer>
    );
  }

  // Error
  if (error || !opportunity) {
    return (
      <ScreenContainer>
        <ScreenHeader title="Opportunity" onBack={handleBack} />
        <View style={styles.center}>
          <Ionicons name="alert-circle-outline" size={icon.xxl} color={color.error} />
          <Text style={styles.errorText}>Opportunity not found</Text>
        </View>
      </ScreenContainer>
    );
  }

  const name = opportunity.name || opportunity.street_address || opportunity.address || 'Unnamed Opportunity';
  const location = formatOpportunityLocation(opportunity);
  const statusConfig = STATUS_CONFIG[opportunity.status] || { label: 'Unknown', color: color.textMuted };
  const hasPartners = opportunity.partners && opportunity.partners.length > 0;
  const listingUrl = opportunity.listing_url || opportunity.zillow_url;
  // IMPORTANT: Keep asking_price and property_value (tax) separate - they are different values!
  const askingPrice = opportunity.asking_price;  // Listing price from seller
  const taxValue = opportunity.property_value;    // Tax assessed value from county
  const canPass = opportunity.status !== 'passed' && opportunity.status !== 'sold';
  const hasParcelData = !!(opportunity.parcel_id || opportunity.owner_name || (opportunity.acreage && opportunity.acreage > 0));

  return (
    <ScreenContainer>
      <ScreenHeader title={name} onBack={handleBack} onMore={handleMore} />

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={async () => { setRefreshing(true); await fetchOpportunity(); setRefreshing(false); }} tintColor={color.primary} />}
      >
        {/* PHOTO GALLERY */}
        <PhotoGallery portfolioId={portId} opportunityId={oppId} />

        {/* VOICE NOTES */}
        <VoiceNotes portfolioId={portId} opportunityId={oppId} />

        {/* FIELD NOTES */}
        <FieldNotes portfolioId={portId} opportunityId={oppId} />

        {/* LOCATION & STATUS */}
        {location && <Text style={styles.location}>{location}</Text>}
        <View style={styles.statusRow}>
          <View style={[styles.statusBadge, { backgroundColor: statusConfig.color + '15' }]}>
            <View style={[styles.statusDot, { backgroundColor: statusConfig.color }]} />
            <Text style={[styles.statusText, { color: statusConfig.color }]}>{statusConfig.label}</Text>
          </View>
          {canPass && (
            <Pressable onPress={handlePass} style={styles.passButton}>
              <Ionicons name="close-circle-outline" size={icon.sm} color={color.textMuted} />
              <Text style={styles.passText}>Pass</Text>
            </Pressable>
          )}
        </View>

        {/* PASS REASON */}
        {opportunity.status === 'passed' && opportunity.pass_reason && (
          <View style={styles.passReasonCard}>
            <Text style={styles.passReasonLabel}>Reason for passing:</Text>
            <Text style={styles.passReasonText}>{opportunity.pass_reason}</Text>
          </View>
        )}

        {/* PRICE OVERVIEW */}
        <View style={styles.priceSection}>
          {/* Asking Price Row */}
          <View style={styles.priceRow}>
            <Text style={styles.priceLabel}>Asking Price</Text>
            <View style={styles.priceValues}>
              <Text style={styles.priceTotal}>{formatCurrency(opportunity.asking_price)}</Text>
              {opportunity.acreage && opportunity.asking_price ? (
                <Text style={styles.pricePerAcre}>{formatPricePerAcre(opportunity.asking_price, opportunity.acreage)}</Text>
              ) : null}
            </View>
          </View>
          {/* Tax Assessed Row */}
          <View style={styles.priceRow}>
            <Text style={styles.priceLabel}>Tax Assessed</Text>
            <View style={styles.priceValues}>
              <Text style={styles.priceTotal}>{formatCurrency(opportunity.property_value)}</Text>
              {opportunity.acreage && opportunity.property_value ? (
                <Text style={styles.pricePerAcre}>{formatPricePerAcre(opportunity.property_value, opportunity.acreage)}</Text>
              ) : null}
            </View>
          </View>
          {/* Acreage Row */}
          <View style={styles.priceRow}>
            <Text style={styles.priceLabel}>Acreage</Text>
            <Text style={styles.priceTotal}>
              {opportunity.acreage ? `${opportunity.acreage.toFixed(2)} acres` : '—'}
            </Text>
          </View>
        </View>

        {/* VIEW ON MAP BUTTON */}
        <Pressable 
          onPress={() => router.push({ 
            pathname: '/v2/property/opportunity-map',
            params: { portfolioId: portId.toString(), opportunityId: oppId.toString(), opportunityName: name },
          })} 
          style={styles.viewMapButton}
        >
          <Ionicons name="map-outline" size={icon.md} color={color.primary} />
          <Text style={styles.viewMapText}>View on Map</Text>
          <Ionicons name="chevron-forward" size={icon.sm} color={color.primary} style={{ marginLeft: 'auto' }} />
        </Pressable>

        {/* PARCEL DATA STATUS */}
        {!hasParcelData ? (
          <Pressable onPress={handleLinkParcel} style={styles.parcelWarning}>
            <Ionicons name="layers-outline" size={icon.md} color={color.warning} />
            <View style={styles.parcelWarningContent}>
              <Text style={styles.parcelWarningTitle}>Parcel data not linked</Text>
              <Text style={styles.parcelWarningSubtitle}>Tap to fetch from county records</Text>
            </View>
            {linkingParcel ? (
              <ActivityIndicator size="small" color={color.warning} />
            ) : (
              <Ionicons name="chevron-forward" size={icon.sm} color={color.textMuted} />
            )}
          </Pressable>
        ) : (
          /* PARCEL DETAILS CARD */
          <View style={styles.parcelCard}>
            <View style={styles.parcelHeader}>
              <Ionicons name="layers" size={icon.md} color={color.primary} />
              <Text style={styles.parcelTitle}>Parcel Data</Text>
              <Pressable onPress={handleRefreshParcel} style={styles.parcelRefresh} disabled={linkingParcel}>
                {linkingParcel ? (
                  <ActivityIndicator size="small" color={color.primary} />
                ) : (
                  <Ionicons name="refresh" size={icon.sm} color={color.primary} />
                )}
              </Pressable>
            </View>
            {opportunity.parcel_id && (
              <ParcelDetail label="Parcel ID" value={opportunity.parcel_id} />
            )}
            {opportunity.owner_name && (
              <ParcelDetail label="Owner" value={opportunity.owner_name} />
            )}
            {opportunity.county && (
              <ParcelDetail label="County" value={opportunity.county} />
            )}
            {opportunity.city && (
              <ParcelDetail label="City" value={opportunity.city} />
            )}
            {/* County Record Links */}
            <CountyRecordLinks county={opportunity.county} parcelId={opportunity.parcel_id} />
          </View>
        )}

        {/* LISTING LINK */}
        {listingUrl && (
          <Pressable onPress={handleListing} style={styles.listingButton}>
            <Ionicons name="open-outline" size={icon.md} color={color.info} />
            <Text style={styles.listingText}>View Listing</Text>
          </Pressable>
        )}

        {/* OWNERS */}
        {hasPartners && (
          <View style={styles.section}>
            <SectionHeader label="Owners" />
            <ListCard>
              {opportunity.partners!.map((partner, i) => (
                <ListRow
                  key={partner.user_id}
                  iconName="person-outline"
                  title={(partner as any).display_name || partner.email}
                  subtitle={`${partner.ownership_percentage}% ownership${(partner as any).display_name ? ` · ${partner.email}` : ''}`}
                  onPress={() => {}}
                  showChevron={false}
                  hideBorder={i === opportunity.partners!.length - 1}
                />
              ))}
            </ListCard>
          </View>
        )}

        {/* IDEAS */}
        <View style={styles.section}>
          <SectionHeader label="Ideas" />
          <Pressable 
            style={styles.ideasPlaceholder} 
            onPress={() => router.push({
              pathname: '/v2/property/ideas',
              params: { opportunityId: oppId.toString(), opportunityName: name },
            })}
          >
            <Ionicons name="bulb-outline" size={icon.lg} color={color.primary} />
            <View style={styles.ideasContent}>
              <Text style={styles.ideasTitle}>Share ideas about this opportunity</Text>
              <Text style={styles.ideasSubtitle}>Vote and discuss with partners</Text>
            </View>
            <Ionicons name="chevron-forward" size={icon.sm} color={color.textMuted} />
          </Pressable>
        </View>

        {/* NAVIGATION */}
        <View style={styles.section}>
          <SectionHeader label="Details" />
          <ListCard>
            <ListRow 
              iconName="checkbox-outline" 
              title="Due Diligence" 
              subtitle="Checklist and tasks" 
              onPress={() => router.push({
                pathname: '/v2/property/due-diligence',
                params: { portfolioId: portId.toString(), opportunityId: oppId.toString(), opportunityName: name },
              })} 
            />
            <ListRow 
              iconName="calculator-outline" 
              title="Financials" 
              subtitle="Mortgage calculator" 
              onPress={() => router.push({
                pathname: '/v2/property/financials',
                params: { 
                  opportunityName: name, 
                  askingPrice: String(askingPrice || 0),
                  acreage: String(opportunity.acreage || 0),
                },
              })} 
            />
            <ListRow 
              iconName="layers-outline" 
              title="Parcels & Neighbors" 
              subtitle="L1/L2/L3 adjacent properties" 
              onPress={() => router.push({
                pathname: '/v2/property/parcels',
                params: { portfolioId: portId.toString(), opportunityId: oppId.toString(), opportunityName: name },
              })} 
            />
            <ListRow 
              iconName="chatbubbles-outline" 
              title="Activity" 
              subtitle="Comments, history" 
              onPress={() => router.push({
                pathname: '/v2/property/activity',
                params: { portfolioId: portId.toString(), opportunityId: oppId.toString(), opportunityName: name },
              })} 
            />
            <ListRow 
              iconName="people-outline" 
              title="Address Book" 
              subtitle="Contacts" 
              onPress={() => router.push({
                pathname: '/v2/property/contacts',
                params: { portfolioId: portId.toString(), opportunityId: oppId.toString(), opportunityName: name },
              })} 
              hideBorder 
            />
          </ListCard>
        </View>
      </ScrollView>

      {/* ACTIONS MENU */}
      <OpportunityActionsMenu
        visible={showActionsMenu}
        onClose={() => setShowActionsMenu(false)}
        onEdit={handleEditRequest}
        onLinkParcel={handleLinkParcel}
        onRefreshParcel={handleRefreshParcel}
        onDelete={handleDeleteRequest}
        linkingParcel={linkingParcel}
        hasParcelData={hasParcelData}
      />

      {/* EDIT MODAL */}
      <EditOpportunityModal
        visible={showEditModal}
        opportunity={opportunity}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveEdit}
        saving={saving}
      />

      {/* DELETE CONFIRMATION */}
      <DeleteConfirmModal
        visible={showDeleteConfirm}
        opportunityName={name}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        deleting={deleting}
      />

      {/* PASS MODAL - Web-compatible alternative to Alert.prompt */}
      <PassOpportunityModal
        visible={showPassModal}
        onCancel={() => setShowPassModal(false)}
        onConfirm={async (reason) => {
          const result = await passOpportunity(reason);
          if (result.success) {
            Alert.alert('Success', 'Opportunity marked as passed');
            setShowPassModal(false);
            await fetchOpportunity();
          } else {
            Alert.alert('Error', result.error || 'Failed to mark opportunity as passed');
          }
        }}
      />
    </ScreenContainer>
  );
}
